import { ASCENDING, DESCENDING } from 'constants/sortDirections';
import { selectIsGlobalSmsFirstPartyUIFeatureFlagOn } from 'core/userContext';
import { SmsCountryPreferences } from 'modules/graphql';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Help, Search } from 'ui/icons';
import { Table } from 'ui/table';
import { TableRows } from 'ui/tableRows';
import { colors } from 'ui/theme';
import { DISPLAY_CONTENT, FIELDS } from '../constants';
import { useQuerySmsPreferencesByCountry } from '../hooks';
import { Column, Field, FieldId, OptOutType, Row, SortableFields } from '../types';
import { isUnitedStatesOrCanada } from '../utils';
import { OverflowSelect } from './OverflowSelect';
import { FrequencyCell, InstructionsCell, LastModifiedCell, RegionCell } from './TableCells';
import {
  ContentHeader,
  ContentHeaderTitle,
  FieldColumnTitle,
  RowCell,
  SearchField,
  StyledTableHeader,
  StyledTooltip,
  TableWrapper,
} from './styles';

const { headerTitle, headerTitleTooltip, searchFieldPlaceholder } = DISPLAY_CONTENT.contentTable;

const FALLBACK_SETTING_VALUE = '_default';
const FALLBACK_TIMESTAMP_VALUE = '0';

export const ContentTable = () => {
  const { results: allCountryPreferences } = useQuerySmsPreferencesByCountry();
  const isGlobalSmsFirstPartyEnabled = useSelector(selectIsGlobalSmsFirstPartyUIFeatureFlagOn);

  const [searchValue, setSearchValue] = useState('');
  const [sortedBy, setSortedBy] = useState<SortableFields>(FieldId.COUNTRY);
  const [sortDirection, setSortDirection] = useState(ASCENDING);

  const columnWidthsRef = useRef<{ [key: string]: number } | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const getSettingValue = (settings: SmsCountryPreferences, field: SortableFields) =>
    field === FieldId.UPDATED_AT
      ? (settings[field]?.timestamp ?? FALLBACK_TIMESTAMP_VALUE)
      : (settings[sortedBy] ?? FALLBACK_SETTING_VALUE);

  const sortedOptOutSettings: SmsCountryPreferences[] = useMemo(
    () =>
      allCountryPreferences
        .filter(
          (setting) =>
            (isGlobalSmsFirstPartyEnabled || isUnitedStatesOrCanada(setting.regionCode)) &&
            setting.regionName.toLowerCase().includes(searchValue.toLowerCase()),
        )
        .sort((settingA: SmsCountryPreferences, settingB: SmsCountryPreferences) => {
          const valueA = getSettingValue(settingA, sortedBy);
          const valueB = getSettingValue(settingB, sortedBy);

          if (sortDirection === DESCENDING) return valueA > valueB ? -1 : 1;
          return valueB > valueA ? -1 : 1;
        }),
    [allCountryPreferences, sortedBy, sortDirection, searchValue],
  );

  const createColumnData = ({ fieldName, fieldId, sortable, tooltipContent }: Field): Column => ({
    name: (
      <FieldColumnTitle>
        {fieldName}
        {tooltipContent && (
          <StyledTooltip tooltipContent={tooltipContent}>
            <Help color={colors.textSecondary} height={16} width={16} />
          </StyledTooltip>
        )}
      </FieldColumnTitle>
    ),
    value: fieldId,
    align: 'flex-start',
    sortable,
    RowCell: ({ row }) => <RowCell>{row[fieldId]}</RowCell>,
  });

  const tableColumns: Column[] = FIELDS.map((field) => createColumnData(field));

  const tableRows: Row[] = sortedOptOutSettings.map(
    ({
      regionCode,
      regionName,
      alphanumericOptOut,
      numericOptOut,
      frequency,
      updatedAt,
      updatedBy,
    }) => {
      const setting = {
        regionCode,
        regionName,
        alphanumericOptOut,
        numericOptOut,
        frequency,
      };

      return {
        id: regionCode,
        regionName: <RegionCell regionName={regionName} regionCode={regionCode} />,
        alphanumeric: (
          <InstructionsCell
            regionName={regionName}
            instructions={alphanumericOptOut}
            type={OptOutType.ALPHANUMERIC}
          />
        ),
        numeric: (
          <InstructionsCell
            regionName={regionName}
            instructions={numericOptOut}
            type={OptOutType.NUMERIC}
          />
        ),
        frequency: <FrequencyCell frequency={frequency} />,
        updatedAt: <LastModifiedCell updatedAt={updatedAt} updatedBy={updatedBy} />,
        actions: <OverflowSelect {...setting} />,
      };
    },
  );

  /**
   * Determine initial width of columns and set width value in component
   * so that column width does not change when displayed content changes.
   */
  useEffect(() => {
    if (sortedOptOutSettings?.length)
      columnWidthsRef.current = tableColumns.reduce(
        (widths: { [key: string]: number }, { value }) => {
          const el = tableRef.current?.querySelector(`#header-cell-${value}`);
          return el ? { ...widths, [value]: (el as HTMLElement).offsetWidth } : widths;
        },
        {},
      );
  }, [sortedOptOutSettings]);

  return (
    <div data-test="global-sms-settings-content-table-container">
      <ContentHeader data-test="global-sms-settings-content-header">
        <ContentHeaderTitle data-test="global-sms-settings-content-header-title">
          {headerTitle}
          <StyledTooltip tooltipContent={headerTitleTooltip}>
            <Help color={colors.textSecondary} height={20} width={20} />
          </StyledTooltip>
        </ContentHeaderTitle>
        <SearchField
          data-test="global-sms-settings-content-table-search-field"
          onChange={(val) => setSearchValue(val)}
          placeholder={searchFieldPlaceholder}
          Prefix={<Search isColorInherited />}
          value={searchValue}
        />
      </ContentHeader>
      <TableWrapper ref={tableRef}>
        <Table data-test="global-sms-settings-content-table">
          <StyledTableHeader
            data-test="global-sms-settings-content-table-header"
            columns={tableColumns}
            sortedBy={sortedBy}
            setSortedBy={(val: string) => setSortedBy(val as SortableFields)}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            $columnWidths={columnWidthsRef.current}
          />
          <TableRows columns={tableColumns} rows={tableRows} />
        </Table>
      </TableWrapper>
    </div>
  );
};
